import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { parseAsString } from 'nuqs';

import { useEntitlements } from '@/hooks/useEntitlements';
import { useResourceQueryState as useQueryState } from '@/hooks/useResourceQueryState';
import {
    buildListFilterJsonLogic,
    ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { filtersQuery } from '@/queries';
import { SavedFilterDefinition, SavedFilterResourceType, savedFilterService } from '@/services/database/savedFilters';
import { type AdvancedFilterDefinition } from '@/shared/components/filters';
import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';

import { normalizeWorkspaceResourceFilterDefinition } from './workspaceResourceFilters';

interface UseWorkspaceResourceFiltersOptions {
    enabled?: boolean;
    resourceId?: string | null;
    resourceType: SavedFilterResourceType;
}

export interface WorkspaceResourceFilterUpdateOptions {
    savedFilterId?: string | null;
}

export type WorkspaceResourceFilterSetter = (
    nextFilters: ListFilterRule[] | null,
    options?: WorkspaceResourceFilterUpdateOptions,
) => void;

export type WorkspaceResourceAdvancedFilterSetter = (
    nextAdvancedFilter: AdvancedFilterDefinition<ListFilterRule> | null,
    options?: WorkspaceResourceFilterUpdateOptions,
) => void;

export type WorkspaceResourceAdvancedFiltersSetter = (
    nextAdvancedFilters: AdvancedFilterDefinition<ListFilterRule>[],
    options?: WorkspaceResourceFilterUpdateOptions,
) => void;

export type WorkspaceResourceFilterDefinitionSetter = (
    nextFilters: ListFilterRule[] | null,
    nextAdvancedFilter: AdvancedFilterDefinition<ListFilterRule> | null,
    options?: WorkspaceResourceFilterUpdateOptions,
) => void;

const savedFilterIdQuery = parseAsString.withOptions({ history: 'replace', shallow: true });
const emptyDefinition: SavedFilterDefinition<ListFilterRule> = { rules: [] };

const buildDefinition = (
    rules: ListFilterRule[],
    advancedFilters: AdvancedFilterDefinition<ListFilterRule>[] = [],
): SavedFilterDefinition<ListFilterRule> => ({
    ...(advancedFilters.length ? { advancedFilters } : {}),
    jsonLogic: buildListFilterJsonLogic(rules, advancedFilters),
    rules,
});

const getDefinitionAdvancedFilters = (definition: SavedFilterDefinition<ListFilterRule>) =>
    definition.advancedFilters ?? (definition.advancedFilter ? [definition.advancedFilter] : []);

export const useWorkspaceResourceFilters = ({
    enabled = true,
    resourceId,
    resourceType,
}: UseWorkspaceResourceFiltersOptions) => {
    const { hasFeature } = useEntitlements();
    const routePreview = useResourceRoutePreview();
    const canUseAdvancedFilters = hasFeature('advancedFilters');
    const [savedFilterId, setSavedFilterId] = useQueryState('filter', savedFilterIdQuery);
    const [legacyFilters, setLegacyFilters] = useQueryState('filters', filtersQuery);
    const [definition, setDefinition] = useState<SavedFilterDefinition<ListFilterRule>>(emptyDefinition);
    const definitionRef = useRef<SavedFilterDefinition<ListFilterRule>>(emptyDefinition);
    const [hydratedResourceKey, setHydratedResourceKey] = useState<string>();
    const activeResourceKeyRef = useRef<string>();
    const mutationVersionRef = useRef(0);
    const savedFilterIdRef = useRef(savedFilterId);
    const suppressedHydrationRef = useRef<string>();
    const suppressedStoredHydrationRef = useRef<string>();

    const resourceKey = resourceId ? `${resourceType}:${resourceId}` : undefined;
    const hydrated = Boolean(resourceKey && hydratedResourceKey === resourceKey);

    useEffect(() => {
        savedFilterIdRef.current = savedFilterId;
    }, [savedFilterId]);

    useEffect(() => {
        definitionRef.current = definition;
    }, [definition]);

    const updateSavedFilterQuery = useCallback(
        (nextSavedFilterId: string | null) => {
            if (!resourceKey || savedFilterIdRef.current === nextSavedFilterId) {
                return;
            }

            savedFilterIdRef.current = nextSavedFilterId;
            suppressedHydrationRef.current = `${resourceKey}:${nextSavedFilterId ?? ''}`;
            void setSavedFilterId(nextSavedFilterId);
        },
        [resourceKey, setSavedFilterId],
    );

    const persistDefinition = useCallback(
        async (nextDefinition: SavedFilterDefinition<ListFilterRule>, nextSavedFilterId?: string | null) => {
            if (routePreview) {
                return true;
            }
            if (!resourceId) {
                return false;
            }

            try {
                await savedFilterService.updateState<ListFilterRule>({
                    definition: nextDefinition,
                    resourceId,
                    resourceType,
                    savedFilterId: nextSavedFilterId ?? null,
                });

                return true;
            } catch (error) {
                captureException(error, {
                    tags: {
                        feature: 'workspace-resource-filters',
                        resourceType,
                    },
                });

                return false;
            }
        },
        [resourceId, resourceType, routePreview],
    );

    const commitFilterDefinition = useCallback(
        (
            nextFilters: ListFilterRule[] | null,
            nextAdvancedFilters: AdvancedFilterDefinition<ListFilterRule>[],
            options?: WorkspaceResourceFilterUpdateOptions,
        ) => {
            const nextDefinition = buildDefinition(nextFilters ?? [], canUseAdvancedFilters ? nextAdvancedFilters : []);
            const nextSavedFilterId = options?.savedFilterId ?? null;
            const previousDefinition = definitionRef.current;
            const previousSavedFilterId = savedFilterIdRef.current;
            const mutationVersion = mutationVersionRef.current + 1;

            activeResourceKeyRef.current = resourceKey;
            mutationVersionRef.current = mutationVersion;
            definitionRef.current = nextDefinition;
            setDefinition(nextDefinition);
            setHydratedResourceKey(resourceKey);
            updateSavedFilterQuery(nextSavedFilterId);
            void persistDefinition(nextDefinition, nextSavedFilterId).then((persisted) => {
                if (
                    persisted ||
                    mutationVersionRef.current !== mutationVersion ||
                    activeResourceKeyRef.current !== resourceKey
                ) {
                    return;
                }

                mutationVersionRef.current += 1;
                definitionRef.current = previousDefinition;
                setDefinition(previousDefinition);
                setHydratedResourceKey(resourceKey);
                updateSavedFilterQuery(previousSavedFilterId);
            });
        },
        [canUseAdvancedFilters, persistDefinition, resourceKey, updateSavedFilterQuery],
    );

    const setFilterDefinition = useCallback<WorkspaceResourceFilterDefinitionSetter>(
        (nextFilters, nextAdvancedFilter, options) =>
            commitFilterDefinition(nextFilters, nextAdvancedFilter ? [nextAdvancedFilter] : [], options),
        [commitFilterDefinition],
    );

    const setFilters = useCallback<WorkspaceResourceFilterSetter>(
        (nextFilters, options) =>
            commitFilterDefinition(nextFilters, getDefinitionAdvancedFilters(definitionRef.current), options),
        [commitFilterDefinition],
    );

    const setAdvancedFilter = useCallback<WorkspaceResourceAdvancedFilterSetter>(
        (nextAdvancedFilter, options) => setFilterDefinition(definitionRef.current.rules, nextAdvancedFilter, options),
        [setFilterDefinition],
    );

    const setAdvancedFilters = useCallback<WorkspaceResourceAdvancedFiltersSetter>(
        (nextAdvancedFilters, options) =>
            commitFilterDefinition(definitionRef.current.rules, nextAdvancedFilters, options),
        [commitFilterDefinition],
    );

    useEffect(() => {
        if (!enabled || !resourceId || !resourceKey) {
            return;
        }

        const querySelectionKey = `${resourceKey}:${savedFilterId ?? ''}`;
        const hasLegacyDefinition = new URLSearchParams(window.location.search).has('filters');

        if (suppressedHydrationRef.current === querySelectionKey) {
            suppressedHydrationRef.current = undefined;
            return;
        }

        if (!hasLegacyDefinition && suppressedStoredHydrationRef.current === resourceKey) {
            suppressedStoredHydrationRef.current = undefined;
            return;
        }

        const controller = new AbortController();
        const requestMutationVersion = mutationVersionRef.current;

        activeResourceKeyRef.current = resourceKey;

        const canCommitRequest = () =>
            !controller.signal.aborted &&
            activeResourceKeyRef.current === resourceKey &&
            mutationVersionRef.current === requestMutationVersion;

        const commitDefinition = (
            nextDefinition: SavedFilterDefinition<ListFilterRule>,
            nextSavedFilterId: string | null,
        ) => {
            if (!canCommitRequest()) {
                return;
            }

            definitionRef.current = nextDefinition;
            setDefinition(nextDefinition);
            setHydratedResourceKey(resourceKey);
            updateSavedFilterQuery(nextSavedFilterId);
        };

        const hydrateStoredState = async () => {
            const state = await savedFilterService.getState<ListFilterRule>(
                { resourceId, resourceType },
                controller.signal,
            );
            const storedDefinition = normalizeWorkspaceResourceFilterDefinition(state.definition ?? emptyDefinition);

            commitDefinition(storedDefinition, state.savedFilterId ?? null);
        };

        if (hasLegacyDefinition) {
            const legacyDefinition = normalizeWorkspaceResourceFilterDefinition(legacyFilters);

            mutationVersionRef.current += 1;
            suppressedStoredHydrationRef.current = resourceKey;
            queueMicrotask(() => {
                if (controller.signal.aborted) {
                    return;
                }

                definitionRef.current = legacyDefinition;
                setDefinition(legacyDefinition);
                setHydratedResourceKey(resourceKey);
                updateSavedFilterQuery(null);
                void setLegacyFilters(null, { history: 'replace', shallow: true });
                void persistDefinition(legacyDefinition, null);
            });

            return () => controller.abort();
        }

        const hydrate = async () => {
            if (savedFilterId) {
                try {
                    const savedFilter = await savedFilterService.resolve<ListFilterRule>(
                        savedFilterId,
                        { resourceId, resourceType },
                        controller.signal,
                    );
                    const resolvedDefinition = normalizeWorkspaceResourceFilterDefinition(savedFilter.definition);

                    commitDefinition(resolvedDefinition, savedFilter.id);

                    if (canCommitRequest()) {
                        void persistDefinition(resolvedDefinition, savedFilter.id);
                    }

                    return;
                } catch (error) {
                    if (controller.signal.aborted) {
                        return;
                    }

                    captureException(error, {
                        tags: {
                            feature: 'saved-filter-reference-resolution',
                            resourceType,
                        },
                    });
                    updateSavedFilterQuery(null);
                }
            }

            await hydrateStoredState();
        };

        void hydrate().catch((error) => {
            if (!controller.signal.aborted && canCommitRequest()) {
                captureException(error, {
                    tags: {
                        feature: 'workspace-resource-filter-hydration',
                        resourceType,
                    },
                });
                commitDefinition(emptyDefinition, null);
            }
        });

        return () => controller.abort();
    }, [
        enabled,
        legacyFilters,
        persistDefinition,
        resourceId,
        resourceKey,
        resourceType,
        savedFilterId,
        setLegacyFilters,
        updateSavedFilterQuery,
    ]);

    const effectiveDefinition = useMemo(() => {
        if (!hydrated) {
            return emptyDefinition;
        }

        return canUseAdvancedFilters ? definition : buildDefinition(definition.rules);
    }, [canUseAdvancedFilters, definition, hydrated]);

    return {
        advancedFilter: getDefinitionAdvancedFilters(effectiveDefinition)[0],
        advancedFilters: getDefinitionAdvancedFilters(effectiveDefinition),
        definition: effectiveDefinition,
        filters: effectiveDefinition.rules,
        hydrated,
        savedFilterId: hydrated ? savedFilterId : null,
        setAdvancedFilter,
        setAdvancedFilters,
        setFilterDefinition,
        setFilters,
    };
};
